import { NOTE_FIELDS, PROPOSED_CHANGE_TYPES, REVIEW_DECISIONS, REVIEW_EXPORT_TYPE, REVIEW_EXPORT_VERSION } from './constants.js'
import { normaliseReview } from './persistence.js'

const decisionValues = new Set(REVIEW_DECISIONS.map(([value]) => value))

export function createReviewExport(artifact, state, now = new Date(), requestedCoverage = 'auto') {
  const exportedAt = now.toISOString()
  const expected = artifact.records.map(({ character_id }) => character_id)
  const reviews = expected.map((character_id) => ({ character_id, ...normaliseReview(state.reviews[character_id]) }))
  const included = reviews.map(({ character_id }) => character_id)
  const missing = expected.filter((id) => !included.includes(id) || !reviews.find((review) => review.character_id === id)?.decision)
  const coverage = requestedCoverage === 'complete' || (requestedCoverage === 'auto' && state.coverage_status !== 'partial' && missing.length === 0) ? 'complete' : 'partial'
  if (coverage === 'complete' && missing.length) throw new Error(`A complete export requires decisions for all characters. Missing: ${missing.join(', ')}`)
  if (coverage === 'complete' && !state.reviewer_reference?.trim()) throw new Error('Reviewer reference is required for a complete export.')
  return {
    format: REVIEW_EXPORT_TYPE,
    format_version: REVIEW_EXPORT_VERSION,
    source_pack_version: artifact.source_pack_version,
    contract_version: artifact.contract_version,
    artifact_version: artifact.artifact_version,
    projection_digest: artifact.projection_digest,
    review_session_id: state.review_session_id,
    reviewer_reference: state.reviewer_reference?.trim() || '',
    reviewer_display_name: state.reviewer_display_name?.trim() || '',
    review_started_at: state.review_started_at,
    review_updated_at: exportedAt,
    exported_at: exportedAt,
    coverage_status: coverage,
    expected_character_ids: expected,
    included_character_ids: included,
    missing_character_ids: missing,
    character_count_expected: expected.length,
    character_count_included: included.length,
    reviews,
  }
}

export function validateReviewImport(value, artifact) {
  if (!value || value.format !== REVIEW_EXPORT_TYPE || value.format_version !== REVIEW_EXPORT_VERSION) throw new Error('This is not a supported Quercus character review file.')
  if (value.source_pack_version !== artifact.source_pack_version || value.contract_version !== artifact.contract_version || value.artifact_version !== artifact.artifact_version || value.projection_digest !== artifact.projection_digest) throw new Error('The review file targets a stale or incompatible source projection.')
  if (!value.review_session_id || !value.review_started_at || !value.review_updated_at || !value.exported_at) throw new Error('The review file is missing review-session identity.')
  if (!['partial', 'complete'].includes(value.coverage_status)) throw new Error('The review file has an invalid coverage status.')
  if (!Array.isArray(value.reviews)) throw new Error('The review file does not contain a reviews array.')
  const known = new Set(artifact.records.map((record) => record.character_id))
  const seen = new Set()
  const reviews = {}
  for (const review of value.reviews) {
    if (!review || !known.has(review.character_id)) throw new Error(`Unknown character ID: ${review?.character_id || '(missing)'}`)
    if (seen.has(review.character_id)) throw new Error(`Duplicate character ID: ${review.character_id}`)
    if (review.decision && !decisionValues.has(review.decision)) throw new Error(`Unsupported review decision for ${review.character_id}`)
    for (const field of NOTE_FIELDS) if (review[field] !== undefined && typeof review[field] !== 'string') throw new Error(`Invalid ${field} for ${review.character_id}`)
    for (const change of review.proposed_changes || []) {
      const specification = PROPOSED_CHANGE_TYPES[change?.change_type]
      if (!specification || change.target_entity !== specification.entity || !specification.fields.includes(change.field)) throw new Error(`Unsupported structured change for ${review.character_id}`)
      if (!change.target_id || !change.proposed_value || !change.rationale) throw new Error(`Incomplete structured change for ${review.character_id}`)
    }
    seen.add(review.character_id)
    reviews[review.character_id] = normaliseReview(review)
  }
  const expected = artifact.records.map(({ character_id }) => character_id)
  const included = Object.keys(reviews)
  const unexpected = included.filter((id) => !known.has(id))
  const absent = expected.filter((id) => !included.includes(id))
  if (unexpected.length || absent.length) throw new Error(`BFA_REVIEW_SCOPE_VIOLATION: unexpected IDs: ${unexpected.join(', ') || '(none)'}; missing IDs: ${absent.join(', ') || '(none)'}`)
  const missing = expected.filter((id) => !included.includes(id) || !reviews[id].decision)
  if (JSON.stringify(value.expected_character_ids) !== JSON.stringify(expected)
    || JSON.stringify(value.included_character_ids) !== JSON.stringify(included)
    || JSON.stringify(value.missing_character_ids) !== JSON.stringify(missing)
    || value.character_count_expected !== expected.length || value.character_count_included !== included.length) throw new Error('The review file coverage metadata is inconsistent.')
  if (value.coverage_status === 'complete' && (missing.length || !value.reviewer_reference?.trim())) throw new Error('A complete review requires all character decisions and a reviewer reference.')
  return {
    storage_version: 2,
    review_session_id: value.review_session_id,
    reviewer_reference: value.reviewer_reference || '',
    reviewer_display_name: value.reviewer_display_name || '',
    review_started_at: value.review_started_at,
    review_updated_at: value.review_updated_at,
    coverage_status: value.coverage_status,
    artifact_version: value.artifact_version,
    source_pack_version: value.source_pack_version,
    contract_version: value.contract_version,
    projection_digest: value.projection_digest,
    reviews,
  }
}

export function parseReviewImport(text, artifact) {
  let value
  try { value = JSON.parse(text) } catch { throw new Error('The selected file is not valid JSON.') }
  return validateReviewImport(value, artifact)
}

export function downloadReviewExport(value, documentRef = globalThis.document, urlRef = globalThis.URL) {
  const blob = new Blob([`${JSON.stringify(value, null, 2)}\n`], { type: 'application/json' })
  const url = urlRef.createObjectURL(blob)
  const link = documentRef.createElement('a')
  link.href = url
  link.download = `quercus-character-review-${value.exported_at.slice(0, 10)}.json`
  link.click()
  urlRef.revokeObjectURL(url)
}
